import { Box } from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import { useParams } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "../../../../core/app/hooks"
import { uploadReportpdf } from "../../../../core/app/slices/records/xrays"
import { fetchUserProfile } from "../../../../core/app/slices/user/userApis"
import { RootState } from "../../../../core/app/store"
import AttachmentComponent from "../Report/components/Attachment/attachment"
import Auxilliaries from "../Report/components/Auxilliaries/auxilliaries"
import { reportPDFProps } from "../Report/type"

import domtoimage from "dom-to-image-more"
import ReportPDF from "./reportPdf"

import {
  setIsShowProgress,
  setProgressMsg,
} from "@/core/app/slices/clinical/clinicalSlice"
import { dateFormat } from "@/core/utils/formatters"
import { stagingManager } from "@/gluelayer"
import { refinementModule } from "@/gluelayer/core/modules/refinement"
import IPR from "../Report/components/IPR/ipr"
import "../Report/report.scss"

export const ScreeshotReport = forwardRef(
  ({ isReady, toothlist, attachment, iprData, notes }: reportPDFProps, ref) => {
    const { patientData } = useAppSelector(
      (state: RootState) => state.patientService,
    )
    const { caseId } = useParams()

    const { org_name, createDate } = useAppSelector(
      (state: RootState) => state.caseService.caseDetail,
    )
    const { currentRefinementName } = useAppSelector(
      (state: RootState) => state.udTreatService,
    )
    const dispatch = useAppDispatch()

    const attRef = useRef(null)
    const auxRef = useRef(null)
    const iprRef = useRef(null)

    useEffect(() => {
      !org_name && dispatch(fetchUserProfile())
    }, [org_name])

    useImperativeHandle(ref, () => ({
      uploadReportPDF: (status: "open" | "upload") => {
        exportComponentAsPdf(status)
      },
    }))

    const exportComponentAsPdf = async (status: "open" | "upload") => {
      if (attRef.current && auxRef.current && iprRef.current) {
        let currentRefinementDate = ""
        const refinementHistoryList =
          refinementModule.getDetailedHistoryDateInfo()

        if (refinementHistoryList[currentRefinementName] !== "") {
          currentRefinementDate = refinementHistoryList[currentRefinementName]

          if (!currentRefinementDate || currentRefinementDate === "") {
            currentRefinementDate = dateFormat(
              createDate,
              "YYYY-MM-DD HH:mm:ss",
            )
          }
          console.log("🚀 ~ current refinement date:", currentRefinementDate)
        }

        const keypointsUpMaxStep =
          stagingManager.wasmStageData.keypointsUpMaxStep
        const keypointsDownMaxStep =
          stagingManager.wasmStageData.keypointsDownMaxStep

        dispatch(setIsShowProgress(true))
        dispatch(setProgressMsg(""))

        // 计算截图的宽高
        const getCaptureOptions = (element: HTMLElement) => {
          const scale = 2 // 提高清晰度
          return {
            width: element.offsetWidth * scale,
            height: element.offsetHeight * scale,
            style: {
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: `${element.offsetWidth * scale}px`,
              height: `${element.offsetHeight * scale}px`,
            },
          }
        }

        Promise.all([
          domtoimage.toPng(attRef.current, getCaptureOptions(attRef.current)),
          domtoimage.toPng(auxRef.current, getCaptureOptions(auxRef.current)),
          domtoimage.toPng(iprRef.current, getCaptureOptions(iprRef.current)),
        ])
          .then(async ([attDataUrl, auxDataUrl, iprDataUrl]) => {
            const blob = await pdf(
              ReportPDF({
                auxshot: auxDataUrl,
                iprshot: iprDataUrl,
                attshot: attDataUrl,
                patientName: `${patientData.firstName} ${patientData.lastName}`,
                caseID: caseId,
                org_name,
                createDate:
                  currentRefinementDate && currentRefinementDate !== ""
                    ? currentRefinementDate
                    : dateFormat(createDate, "YYYY-MM-DD HH:mm:ss"),
                stages: [keypointsUpMaxStep, keypointsDownMaxStep],
              }),
            ).toBlob()

            if (status === "upload") {
              const newFile = new File([blob], `ipr-att-report.pdf`, {
                type: "image/png",
              })
              const formData = new FormData()
              formData.append(
                "attachment",
                new File([blob], newFile.name, { type: "image/png" }),
              )
              formData.append("original_file_name", "ipr-att-report.pdf")

              dispatch(
                uploadReportpdf({
                  file: blob,
                  patientId: patientData.id,
                  caseId,
                  formData: formData,
                  fileName: newFile.name,
                }),
              )
            } else {
              const url = URL.createObjectURL(blob)

              setTimeout(() => {
                window.open(url, "_blank")
              }, 100)
            }
          })
          .finally(() => {
            dispatch(setIsShowProgress(false))
          })
      }
    }

    return (
      isReady && (
        <Box
          sx={{
            position: "fixed",
            zIndex: -100, //Just need to get the dom element, don't need to display it
          }}
        >
          <Box
            width={710}
            // width={928} // New IPR Report
            height={1080}
            sx={{
              backgroundColor: "#ECEFF1",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "relative",
              }}
              id="attachment"
              ref={attRef}
            >
              <AttachmentComponent
                toothlist={toothlist}
                attachment={attachment}
              />
            </div>

            <div
              style={{
                position: "relative",
              }}
              id="auxilliaries"
              ref={auxRef}
            >
              <Auxilliaries toothlist={toothlist} attachment={attachment} />
            </div>

            <div
              style={{
                position: "relative",
                width: 710,
                height: 703,
                //New IPR Report
                // width: 928,
                // height: "100%",
              }}
              id="ipr"
              ref={iprRef}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  // height: "100%",// New IPR Report
                  height: 663,
                  borderRadius: 4,
                }}
              >
                <IPR toothlist={toothlist} iprData={iprData} isPDF={true} />
              </div>
            </div>
          </Box>
        </Box>
      )
    )
  },
)
ScreeshotReport.displayName = "ScreeshotReport"
